import { GameState } from '../state/GameState';
import { InputRouter, PlayerId } from './InputRouter';
import { EntityManager, Player, Base } from '../entities';

// ============================================
// Step 11: Session Manager (1-4 Player Support)
// ============================================

export type GameMode = '1P' | '2P' | '3P' | '4P';

export interface PlayerSession {
  id: PlayerId;
  enabled: boolean;
  entityId: string | null;
  towersPlaced: number;
  gemsSpent: number;
}

export interface SharedResources {
  coins: number;
  gems: number;
  villagers: number;
  maxVillagers: number;
}

class SessionManagerClass {
  private mode: GameMode = '1P';
  private players: Map<PlayerId, PlayerSession> = new Map();
  private resources: SharedResources = {
    coins: 0,
    gems: 50,
    villagers: 20,
    maxVillagers: 20,
  };

  // Base entity reference for visual sync
  private baseEntity: Base | null = null;

  // Callbacks
  public onCoinsChanged?: (coins: number) => void;
  public onGemsChanged?: (gems: number) => void;
  public onVillagersChanged?: (villagers: number) => void;

  // ============================================
  // Initialization
  // ============================================

  init(mode: GameMode, startingGems: number, startingVillagers: number, startingCoins: number = 0): void {
    this.mode = mode;

    // Initialize player sessions
    this.players.clear();

    this.players.set('P1', {
      id: 'P1',
      enabled: true,
      entityId: null,
      towersPlaced: 0,
      gemsSpent: 0,
    });

    this.players.set('P2', {
      id: 'P2',
      enabled: mode === '2P' || mode === '3P' || mode === '4P',
      entityId: null,
      towersPlaced: 0,
      gemsSpent: 0,
    });

    this.players.set('P3', {
      id: 'P3',
      enabled: mode === '3P' || mode === '4P',
      entityId: null,
      towersPlaced: 0,
      gemsSpent: 0,
    });

    this.players.set('P4', {
      id: 'P4',
      enabled: mode === '4P',
      entityId: null,
      towersPlaced: 0,
      gemsSpent: 0,
    });

    // Initialize shared resources
    this.resources = {
      coins: startingCoins,
      gems: startingGems,
      villagers: startingVillagers,
      maxVillagers: startingVillagers,
    };

    console.log(`[SessionManager] Initialized ${mode} mode with ${startingCoins} coins, ${startingGems} gems, ${startingVillagers} villagers`);
  }

  /**
   * Register a player entity
   */
  registerPlayerEntity(playerId: PlayerId, entityId: string): void {
    const session = this.players.get(playerId);
    if (session) {
      session.entityId = entityId;
    }
  }

  // ============================================
  // Mode Queries
  // ============================================

  getMode(): GameMode {
    return this.mode;
  }

  is2Player(): boolean {
    return this.mode === '2P';
  }

  isPlayerEnabled(playerId: PlayerId): boolean {
    return this.players.get(playerId)?.enabled ?? false;
  }

  getEnabledPlayers(): PlayerId[] {
    const enabled: PlayerId[] = [];
    this.players.forEach((session, id) => {
      if (session.enabled) enabled.push(id);
    });
    return enabled;
  }

  // ============================================
  // Shared Resources
  // ============================================

  getGems(): number {
    return this.resources.gems;
  }

  canAfford(cost: number): boolean {
    return this.resources.gems >= cost;
  }

  canAffordGems(cost: number): boolean {
    return this.resources.gems >= cost;
  }

  spendGems(cost: number, playerId: PlayerId): boolean {
    if (!this.canAfford(cost)) return false;

    this.resources.gems -= cost;

    // Track player stats
    const session = this.players.get(playerId);
    if (session) {
      session.gemsSpent += cost;
    }

    this.onGemsChanged?.(this.resources.gems);
    return true;
  }

  addGems(amount: number): void {
    this.resources.gems += amount;
    this.onGemsChanged?.(this.resources.gems);
  }

  // ============================================
  // Coin Resources
  // ============================================

  getCoins(): number {
    return this.resources.coins;
  }

  canAffordCoins(cost: number): boolean {
    return this.resources.coins >= cost;
  }

  spendCoins(cost: number): boolean {
    if (cost > this.resources.coins) {
      // Spend what we have
      this.resources.coins = 0;
    } else {
      this.resources.coins -= cost;
    }
    this.onCoinsChanged?.(this.resources.coins);
    return true;
  }

  addCoins(amount: number): void {
    this.resources.coins += amount;
    this.onCoinsChanged?.(this.resources.coins);
  }

  getVillagers(): number {
    return this.resources.villagers;
  }

  getMaxVillagers(): number {
    return this.resources.maxVillagers;
  }

  removeVillager(damage: number = 1): number {
    this.resources.villagers = Math.max(0, this.resources.villagers - damage);
    this.onVillagersChanged?.(this.resources.villagers);

    // Sync with base entity visual
    if (this.baseEntity && damage > 0) {
      this.baseEntity.syncVillagers(this.resources.villagers);
    }

    return this.resources.villagers;
  }

  /**
   * Set reference to base entity for visual sync
   */
  setBaseEntity(base: Base): void {
    this.baseEntity = base;
  }

  isDefeated(): boolean {
    return this.resources.villagers <= 0;
  }

  getVillagerPercent(): number {
    return this.resources.villagers / this.resources.maxVillagers;
  }

  // ============================================
  // Player Stats
  // ============================================

  recordTowerPlaced(playerId: PlayerId): void {
    const session = this.players.get(playerId);
    if (session) {
      session.towersPlaced++;
    }
  }

  getPlayerStats(playerId: PlayerId): PlayerSession | null {
    return this.players.get(playerId) ?? null;
  }

  getTotalTowersPlaced(): number {
    let total = 0;
    this.players.forEach(session => {
      total += session.towersPlaced;
    });
    return total;
  }

  getTotalGemsSpent(): number {
    let total = 0;
    this.players.forEach(session => {
      total += session.gemsSpent;
    });
    return total;
  }

  // ============================================
  // Player Entity Helpers
  // ============================================

  getPlayerEntity(playerId: PlayerId): Player | null {
    const session = this.players.get(playerId);
    if (!session?.entityId) return null;
    return EntityManager.get(session.entityId) as Player | null;
  }

  getAllPlayerEntities(): Player[] {
    const players: Player[] = [];
    this.players.forEach(session => {
      if (session.enabled && session.entityId) {
        const player = EntityManager.get(session.entityId) as Player;
        if (player) players.push(player);
      }
    });
    return players;
  }

  /**
   * Get the closest player to a world position
   */
  getClosestPlayer(x: number, y: number): Player | null {
    const players = this.getAllPlayerEntities();
    if (players.length === 0) return null;

    let closest: Player | null = null;
    let closestDist = Infinity;

    for (const player of players) {
      const dist = Phaser.Math.Distance.Between(x, y, player.x, player.y);
      if (dist < closestDist) {
        closestDist = dist;
        closest = player;
      }
    }

    return closest;
  }

  /**
   * Check if any player is within range of a position
   */
  isAnyPlayerInRange(x: number, y: number, range: number): boolean {
    const players = this.getAllPlayerEntities();
    for (const player of players) {
      if (player.distanceTo({ x, y }) <= range) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get the player who is within range (for building)
   */
  getPlayerInRange(x: number, y: number, range: number): Player | null {
    const players = this.getAllPlayerEntities();
    for (const player of players) {
      if (player.distanceTo({ x, y }) <= range) {
        return player;
      }
    }
    return null;
  }

  // ============================================
  // Cleanup
  // ============================================

  reset(): void {
    this.players.forEach(session => {
      session.entityId = null;
      session.towersPlaced = 0;
      session.gemsSpent = 0;
    });

    this.resources = {
      coins: 0,
      gems: 50,
      villagers: 20,
      maxVillagers: 20,
    };

    this.baseEntity = null;
  }
}

// Singleton instance
export const SessionManager = new SessionManagerClass();
